'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          brokerage_name: metadata?.brokerageName || '',
          plan: metadata?.plan || 'professional',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Google OAuth Sign In
  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Reset Password (Forgot Password)
  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?next=/reset-password`,
    });
    if (error) throw error;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Get Agent Knowledge Base (onboarding data) for current user
  const getAgentKnowledgeBase = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('agent_knowledge_base')
      .select('*')
      .eq('user_id', user.id)
      .single();
    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows found
    return data || null;
  };

  // Save agent onboarding data to knowledge base
  const saveAgentKnowledgeBase = async (onboardingData: any) => {
    if (!user) throw new Error('Not authenticated');

    // Ensure user_profiles row exists (in case the trigger didn't fire)
    await supabase
      .from('user_profiles')
      .upsert(
        {
          id: user.id,
          email: user.email || '',
          full_name: `${onboardingData.personal?.firstName || ''} ${onboardingData.personal?.lastName || ''}`.trim() || user.user_metadata?.full_name || '',
          brokerage_name: onboardingData.personal?.brokerageName || '',
        },
        { onConflict: 'id' }
      );

    const payload = {
      user_id: user.id,
      first_name: onboardingData.personal?.firstName || '',
      last_name: onboardingData.personal?.lastName || '',
      phone: onboardingData.personal?.phone || '',
      brokerage_name: onboardingData.personal?.brokerageName || '',
      team_name: onboardingData.personal?.teamName || '',
      license_number: onboardingData.personal?.licenseNumber || '',
      website: onboardingData.personal?.website || '',
      headshot_url: onboardingData.personal?.headshot || '',
      years_in_real_estate: onboardingData.professional?.yearsInRealEstate || '',
      service_areas: onboardingData.professional?.serviceAreas || [],
      specialties: onboardingData.professional?.specialties || [],
      awards: onboardingData.professional?.awards || '',
      certifications: onboardingData.professional?.certifications || [],
      bio: onboardingData.professional?.bio || '',
      communication_style: onboardingData.communicationStyle || [],
      social_connections: onboardingData.socialConnections || {},
      email_connections: onboardingData.emailConnections || {},
      calendar_connections: onboardingData.calendarConnections || {},
      contact_sources: onboardingData.contactSources || {},
      onboarding_completed: true,
    };

    const { data, error } = await supabase
      .from('agent_knowledge_base')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    resetPassword,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    getAgentKnowledgeBase,
    saveAgentKnowledgeBase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
